import { z } from 'zod'

import { isoDateTimeSchema } from './common'

/* Operação (sprint-15-spec §2.5): healthz, readyz, erros do navegador e aviso do monitor. */

/** `GET /api/v1/healthz`: público e raso (não consulta banco nem Redis). */
export const HealthzResponseSchema = z.object({
  status: z.literal('ok'),
  version: z.string(),
  /** Commit da imagem (`GIT_SHA` do build); null fora da imagem de produção. */
  gitSha: z.string().nullable(),
  /** Data do build (`BUILD_TIME`, ISO); null fora da imagem de produção. */
  buildTime: z.string().nullable(),
  uptimeSeconds: z.number().int().min(0),
})
export type HealthzResponse = z.infer<typeof HealthzResponseSchema>

export const READY_STATUSES = ['ok', 'warn', 'fail'] as const
export type ReadyStatus = (typeof READY_STATUSES)[number]

/** Checks do `readyz`, na ordem em que aparecem no cartão Operação. */
export const READY_CHECK_NAMES = [
  'postgres',
  'redis',
  'queues',
  'disk',
  'backup',
  'restoreTest',
  'erpLoad',
  'mail',
  'push',
] as const
export type ReadyCheckName = (typeof READY_CHECK_NAMES)[number]

export const ReadyCheckSchema = z.object({
  status: z.enum(READY_STATUSES),
  /** Frase curta em pt-BR (sem caminho de arquivo, sem segredo). */
  message: z.string(),
  /** Momento de referência do check (último backup, último teste, última carga), quando houver. */
  at: isoDateTimeSchema.nullable().optional(),
})
export type ReadyCheck = z.infer<typeof ReadyCheckSchema>

export const ReadyQueueSchema = z.object({
  name: z.string(),
  waiting: z.number().int().min(0),
  active: z.number().int().min(0),
  delayed: z.number().int().min(0),
  failed: z.number().int().min(0),
  /** Idade do job mais antigo aguardando, em segundos (null sem fila). */
  oldestWaitingSeconds: z.number().int().min(0).nullable(),
})
export type ReadyQueue = z.infer<typeof ReadyQueueSchema>

/** `GET /api/v1/readyz?token=`: 200 sem `fail`, 503 com algum `fail`. */
export const ReadyzResponseSchema = z.object({
  status: z.enum(READY_STATUSES),
  checkedAt: isoDateTimeSchema,
  checks: z.object(
    Object.fromEntries(READY_CHECK_NAMES.map((name) => [name, ReadyCheckSchema])) as Record<
      ReadyCheckName,
      typeof ReadyCheckSchema
    >,
  ),
  /** Detalhe das filas (o check `queues` resume). */
  queues: z.array(ReadyQueueSchema),
  disk: z.object({
    freeBytes: z.number().int().min(0).nullable(),
    totalBytes: z.number().int().min(0).nullable(),
  }),
})
export type ReadyzResponse = z.infer<typeof ReadyzResponseSchema>

/** Tetos do `POST /api/v1/client-errors` (o corpo inteiro tem no máximo 1 KB). */
export const CLIENT_ERROR_LIMITS = Object.freeze({
  maxBodyBytes: 1024,
  message: 300,
  route: 200,
  version: 64,
  stack: 600,
  /** Envios por sessão do navegador. */
  perSession: 5,
  /** Envios por minuto e IP aceitos pela API. */
  perMinute: 10,
})

export const ClientErrorReportSchema = z.object({
  message: z.string().trim().min(1).max(CLIENT_ERROR_LIMITS.message),
  route: z.string().trim().min(1).max(CLIENT_ERROR_LIMITS.route),
  version: z.string().trim().min(1).max(CLIENT_ERROR_LIMITS.version),
  /** Já truncado pelo navegador; a API corta de novo. */
  stack: z.string().max(CLIENT_ERROR_LIMITS.stack).optional(),
})
export type ClientErrorReport = z.infer<typeof ClientErrorReportSchema>

/** `POST /api/v1/ops/notify` (usado pelo monitor.sh com o `HEALTH_TOKEN`). */
export const OpsNotifyRequestSchema = z.object({
  title: z.string().trim().min(1).max(120),
  body: z.string().trim().max(500).optional(),
  severity: z.enum(['info', 'attention', 'critical']).default('critical'),
  /** Estado que o monitor viu (entra na chave de deduplicação). */
  state: z.enum(READY_STATUSES).optional(),
})
export type OpsNotifyRequest = z.infer<typeof OpsNotifyRequestSchema>

export const OpsNotifyResponseSchema = z.object({ notified: z.number().int().min(0) })
export type OpsNotifyResponse = z.infer<typeof OpsNotifyResponseSchema>

const statusFileDate = z.union([isoDateTimeSchema, z.iso.datetime({ local: true })])

/**
 * `backup.json` gravado pelo `infra/vps/backup.sh` (lido read-only pela API). Só os campos que o
 * `readyz` usa são exigidos; o resto (tamanhos, sha256, contagens) passa direto.
 */
export const BackupStatusFileSchema = z.looseObject({
  finishedAt: statusFileDate,
  ok: z.boolean().default(true),
  durationSeconds: z.number().min(0).optional(),
})
export type BackupStatusFile = z.infer<typeof BackupStatusFileSchema>

/** `restore-test.json` gravado pelo `infra/vps/restore-test.sh`. */
export const RestoreTestStatusFileSchema = z.looseObject({
  finishedAt: statusFileDate,
  ok: z.boolean(),
  durationSeconds: z.number().min(0).optional(),
  countsMatch: z.boolean().optional(),
  chainOk: z.boolean().optional(),
})
export type RestoreTestStatusFile = z.infer<typeof RestoreTestStatusFileSchema>
